// Customer Sales Order — Product Family scope (Sep 2026).
//
// The Sales Order now has three levels of navigation, in this order:
//
//     PRODUCT FAMILY  ->  CATEGORY  ->  PRODUCTS
//
// Everything that decides what a family tab means lives in this one
// pure module, so the tab row, the category row, the menu sections and
// the search box can never disagree about which products are in scope.
// Nothing here fetches, and nothing here knows about React.
//
// Pipeline position — this is ADDITIVE, never a replacement:
//
//     tenant / catalog eligibility   (backend: `_base_query`)
//         -> active / available      (backend)
//         -> PRODUCT FAMILY          (this module)
//         -> category context        (this module)
//         -> search                  (lib/productSearch.js)
//         -> render
//
// A product that the backend did not send is not reachable from here,
// so no filter in this file can widen what a customer sees — only
// narrow it. That is what keeps tenant and catalog isolation intact.

// The ALL tab. A sentinel rather than `null`/"" so "no family chosen
// yet" and "the customer chose ALL" can never be confused, and so the
// value is safe to keep in component state and compare directly.
export const FAMILY_ALL = "__all__";

// The canonical customer-facing sequence. A family a tenant has that is
// not named here is NOT hidden — it simply sorts after these, so a
// tenant who adds "Bakery" tomorrow gets a working tab without a code
// change. Compared in normalized form.
export const CANONICAL_FAMILY_ORDER = ["restaurant", "grocery", "vegetables", "meat"];

// The same trim + collapse + casefold rule the backend uses
// (`retail/product_attributes.py::normalize_attribute_value`), so
// "Restaurant", "restaurant" and " RESTAURANT " are one family on both
// sides of the wire.
export function normalizeFamily(value) {
  if (value === null || value === undefined) return "";
  return String(value).replace(/\s+/g, " ").trim().toLowerCase();
}

// Order a tenant's families for the tab row: the canonical ones first,
// in the canonical order, then anything else alphabetically.
export function orderFamilies(values) {
  const seen = new Map();
  for (const raw of values || []) {
    const value = typeof raw === "string" ? raw : (raw && raw.value);
    const key = normalizeFamily(value);
    if (!key || seen.has(key)) continue;
    seen.set(key, { value, normalized: key });
  }
  const canonical = [];
  for (const key of CANONICAL_FAMILY_ORDER) {
    if (seen.has(key)) {
      canonical.push(seen.get(key));
      seen.delete(key);
    }
  }
  const rest = [...seen.values()].sort((a, b) => a.normalized.localeCompare(b.normalized));
  return [...canonical, ...rest];
}

// The tab row itself: ALL first and always, then the tenant's families.
// ALL is a TAB, never a checkbox — selecting a family replaces the
// scope rather than adding to it.
export function buildFamilyTabs(families) {
  return [
    { key: FAMILY_ALL, label: "All", normalized: FAMILY_ALL },
    ...orderFamilies(families).map((f) => ({
      key: f.normalized, label: f.value, normalized: f.normalized,
    })),
  ];
}

// Does one product belong to the active family scope?
//
//   ALL       -> every otherwise-eligible product, INCLUDING legacy
//                products that carry no Product Family at all. That
//                inclusion is the whole point of ALL: an existing menu
//                where nothing has been classified yet must keep
//                working untouched.
//   a family  -> only products EXPLICITLY assigned to it. A blank
//                family is not "everything", so an unclassified
//                product never leaks into a named tab.
export function productInFamily(product, family) {
  if (!family || family === FAMILY_ALL) return true;
  return normalizeFamily((product || {}).product_family) === normalizeFamily(family);
}

// Narrow a category list to one Product Family.
//
// Categories that end up with no product are REMOVED, so the category
// row and the menu never show an empty section — the customer's
// category navigation always corresponds to the family they picked.
// The incoming order is preserved exactly: the payload already arrives
// in the tenant's configured category sequence, and re-sorting the
// filtered result would throw that away (which is precisely what the
// tenant reordered the catalog to stop happening).
//
// The synthetic Favorites tab is never filtered — a customer switching
// to GROCERY must not find their saved Favorites emptied, for the same
// reason searching does not empty them.
export function filterCategoriesByFamily(categories, family) {
  if (!family || family === FAMILY_ALL) return categories || [];
  const out = [];
  for (const cat of categories || []) {
    if (cat && cat.isFavoritesTab) {
      out.push(cat);
      continue;
    }
    const products = (cat.products || []).filter((p) => productInFamily(p, family));
    if (products.length) out.push({ ...cat, products, count: products.length });
  }
  return out;
}

// The families actually represented by a set of loaded products. Used
// as the fallback source for the tab row when the backend sent no
// family facets (a small catalog whose payload already holds every
// product), so the tabs are always derived from real, eligible data
// rather than from a hard-coded list.
export function familiesFromCategories(categories) {
  const seen = new Map();
  for (const cat of categories || []) {
    if (cat && cat.isFavoritesTab) continue;
    for (const p of cat.products || []) {
      const key = normalizeFamily(p && p.product_family);
      if (!key || seen.has(key)) continue;
      seen.set(key, p.product_family);
    }
  }
  return [...seen.values()];
}

// Is the family the customer had selected still a real tab? Used to
// reconcile state after the catalog reloads (or after a tenant removes
// the last product of a family) so the Sales Order can never be left
// filtered to a family that no longer exists — which would render an
// empty menu with no visible reason.
export function resolveActiveFamily(family, tabs) {
  if (!family || family === FAMILY_ALL) return FAMILY_ALL;
  return (tabs || []).some((t) => t.key === family) ? family : FAMILY_ALL;
}
